'use client'
import { createContext, useContext, useState, useEffect } from 'react';
import { getStoredPropertyCode } from '@/utils/propertyManager';

// Create the cart context
export const CartContext = createContext();

// Custom hook for using the cart context
export const useCart = () => useContext(CartContext);

// API Base URL — goes through the server-side proxy (credentials injected there)
const BASE_URL = '/api/proxy';

// Cart provider component
export const CartProvider = ({ children }) => {
  // Initialize state with items from localStorage if available
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [lastOperation, setLastOperation] = useState(null);
  

  // Load cart items from localStorage on component mount
  // Clear cart if property has changed
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        // Get current property from URL or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const currentProperty = urlParams.get('property') || getStoredPropertyCode();
        
        const savedCart = localStorage.getItem('bookingCart');
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          
          // Check if any cart item has a different property
          if (parsedCart && parsedCart.length > 0 && currentProperty) {
            const hasDifferentProperty = parsedCart.some(item => 
              item.property_code && item.property_code !== currentProperty
            );
            
            if (hasDifferentProperty) {
              console.log('🔄 Cart contains items from different property, clearing cart');
              console.log('  Current property:', currentProperty);
              console.log('  Cart items property:', parsedCart.map(i => i.property_code).join(', '));
              localStorage.removeItem('bookingCart');
              localStorage.removeItem('checkoutItems');
              setCartItems([]);
              return;
            }
          }
          
          setCartItems(parsedCart);
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart items to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('bookingCart', JSON.stringify(cartItems));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  }, [cartItems]);


  // Fetch fresh availability data from API
  const fetchRoomAvailability = async (roomId, propertyCode, checkIn, checkOut, adults, children, rooms) => {
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      
      // Add property code header
      if (propertyCode) {
        headers.append("X-Database", propertyCode);
      }

      // Use /rooms/available endpoint (same as api.js)
      const url = `${BASE_URL}/rooms/available?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&room=${rooms}`;
      
      console.log('🔄 Fetching fresh availability from API:', url);
      console.log('📝 Headers:', {
        'X-Database': propertyCode
      });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        redirect: 'follow'
      });

      if (!response.ok) {
        console.error(`❌ API error: ${response.status} ${response.statusText}`);
        return null; // Return null instead of throwing
      }

      const data = await response.json();
      
      // Find the specific room in the response
      if (data.status === 'success' && Array.isArray(data.data)) {
        const roomData = data.data.find(r => r.room_info?.room_id === roomId);
        if (roomData && roomData.availability) {
          const roomsAvailable = roomData.availability.rooms_to_sell || roomData.availability.rooms_available || 0;
          console.log('✅ Fresh availability fetched:', roomsAvailable);
          return roomsAvailable;
        }
      }
      
      console.warn('⚠️ Could not find room in API response, using fallback');
      return null;
    } catch (error) {
      console.error('❌ Error fetching availability:', error.message);
      console.warn('⚠️ Will use availability data from room object instead');
      return null; // Return null to use fallback
    }
  };

  // Add an item to the cart with fresh availability check
  const addToCart = async (room) => {
    try {
      // Get URL parameters for fresh availability check
      let propertyCode = room.property_code;
      let checkIn = room.checkIn;
      let checkOut = room.checkOut;
      let adults = 2;
      let children = 0;
      let rooms = 1;
      
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        propertyCode = urlParams.get('property') || propertyCode;
        checkIn = urlParams.get('checkIn') || checkIn;
        checkOut = urlParams.get('checkOut') || checkOut;
        adults = parseInt(urlParams.get('adults')) || 2;
        children = parseInt(urlParams.get('children')) || 0;
        rooms = parseInt(urlParams.get('room')) || 1;
      }

      // Try to fetch fresh availability from API (with fallback)
      let freshAvailability = null;
      try {
        freshAvailability = await fetchRoomAvailability(
          room.room_info.room_id,
          propertyCode,
          checkIn,
          checkOut,
          adults,
          children,
          rooms
        );
      } catch (apiError) {
        console.warn('⚠️ API fetch failed, using room data:', apiError.message);
      }

      // Use fresh availability if available, otherwise use what was passed from room
      const roomsAvailable = freshAvailability !== null 
        ? freshAvailability 
        : (room.room_info?.rooms_available || room.availability?.rooms_to_sell || room.availability?.rooms_available || 999);

      console.log('=== ADD TO CART (CartContext) ===');
      console.log('Room:', room.room_info?.room_type);
      console.log('Rate details:', room.rate_details);
      console.log('Rate code:', room.rate_details?.ratecode);
      console.log('Include breakfast:', room.rate_details?.include_breakfast);
      console.log('Final rate:', room.rate_details?.final_rate);
      console.log('Fresh availability from API:', freshAvailability);
      console.log('Fallback from room object:', room.availability?.rooms_to_sell || room.room_info?.rooms_available);
      console.log('Final rooms_available:', roomsAvailable);
      console.log('=================================');

      // Generate unique cart item ID based on room_id + ratecode
      // This allows same room with different rate plans (e.g., Room Only vs With Breakfast)
      const cartItemId = `${room.room_info.room_id}_${room.rate_details?.ratecode || 'default'}`;
      const newRoomCartItemId = `${room.room_info.room_id}_${room.rate_details?.ratecode || 'default'}`;
      
      console.log('Cart Item ID:', cartItemId);
      console.log('Rate Code:', room.rate_details?.ratecode);
      
      // Check if room with same room_id AND ratecode already exists in cart
      const existingItemIndex = cartItems.findIndex(
        item => {
          const itemCartId = `${item.room_info.room_id}_${item.rate_details?.ratecode || 'default'}`;
          return itemCartId === newRoomCartItemId;
        }
      );

      if (existingItemIndex >= 0) {
        // If room already exists, replace it (update rate/details) but keep quantity
        const updatedItems = [...cartItems];
        const existingQuantity = updatedItems[existingItemIndex].quantity || 1;
        
        updatedItems[existingItemIndex] = {
          ...room,
          quantity: existingQuantity,
          cartItemId: cartItemId,
          // CRITICAL: Explicitly preserve rate_details to prevent fallback to default
          rate_details: {
            ...room.rate_details,
            ratecode: room.rate_details?.ratecode,
            ratedesc: room.rate_details?.ratedesc,
            include_breakfast: room.rate_details?.include_breakfast,
            breakfast_rate: room.rate_details?.breakfast_rate,
            final_rate: room.rate_details?.final_rate,
            benefits: room.rate_details?.benefits,
            requirements: room.rate_details?.requirements,
            color: room.rate_details?.color
          },
          room_info: {
            ...room.room_info,
            rooms_available: roomsAvailable
          },
          // Store promo info if applicable
          promo: room.rate_details?.promo || room.promo || 0,
          activePromo: room.activePromo || null,
          promoDiscount: room.promoDiscount || 0,
          promoFinalPrice: room.promoFinalPrice || room.rate_details?.final_rate,
          promoDiscountType: room.promoDiscountType || null,
          promoTotalDiscount: room.promoTotalDiscount || undefined,
          promoIsPerNight: room.promoIsPerNight || undefined
        };
        
        console.log('✅ Updated cart item with fresh rooms_available:', roomsAvailable);
        console.log('✅ Rate details preserved:', updatedItems[existingItemIndex].rate_details);
        setCartItems(updatedItems);
      } else {
        // Otherwise add as new item with quantity 1
        const newItem = {
          ...room,
          quantity: 1,
          cartItemId: cartItemId,
          // CRITICAL: Explicitly preserve rate_details to prevent fallback to default
          rate_details: {
            ...room.rate_details,
            ratecode: room.rate_details?.ratecode,
            ratedesc: room.rate_details?.ratedesc,
            include_breakfast: room.rate_details?.include_breakfast,
            breakfast_rate: room.rate_details?.breakfast_rate,
            final_rate: room.rate_details?.final_rate,
            benefits: room.rate_details?.benefits,
            requirements: room.rate_details?.requirements,
            color: room.rate_details?.color
          },
          room_info: {
            ...room.room_info,
            rooms_available: roomsAvailable
          },
          // Store promo info if applicable
          promo: room.rate_details?.promo || room.promo || 0,
          activePromo: room.activePromo || null,
          promoDiscount: room.promoDiscount || 0,
          promoFinalPrice: room.promoFinalPrice || room.rate_details?.final_rate,
          promoDiscountType: room.promoDiscountType || null,
          promoTotalDiscount: room.promoTotalDiscount || undefined,
          promoIsPerNight: room.promoIsPerNight || undefined
        };
        console.log('✅ Adding new cart item with fresh rooms_available:', roomsAvailable);
        console.log('✅ Rate details preserved:', newItem.rate_details);
        setCartItems(prev => [...prev, newItem]);
      }
      
      // Update last operation for UI refresh
      setLastOperation({
        type: 'add',
        roomId: room.room_info.room_id,
        cartItemId: cartItemId,
        success: true,
        timestamp: new Date().getTime()
      });
      
      // Open cart when item is added
      setIsCartOpen(true);
    } catch (error) {
      console.error('❌ Error adding room to cart:', error);
      // Fallback: Still add the room to cart with available data
      const cartItemId = `${room.room_info.room_id}_${room.rate_details?.ratecode || 'default'}`;
      
      const existingItemIndex = cartItems.findIndex(
        item => {
          const itemCartId = `${item.room_info.room_id}_${item.rate_details?.ratecode || 'default'}`;
          return itemCartId === cartItemId;
        }
      );
      
      const fallbackAvailability = room.room_info?.rooms_available || room.availability?.rooms_to_sell || 999;
      
      if (existingItemIndex >= 0) {
        const updatedItems = [...cartItems];
        const existingQuantity = updatedItems[existingItemIndex].quantity || 1;
        
        updatedItems[existingItemIndex] = {
          ...room,
          quantity: existingQuantity,
          cartItemId: cartItemId,
          room_info: {
            ...room.room_info,
            rooms_available: fallbackAvailability
          }
        };
        setCartItems(updatedItems);
      } else {
        const newItem = {
          ...room,
          quantity: 1,
          cartItemId: cartItemId,
          room_info: {
            ...room.room_info,
            rooms_available: fallbackAvailability
          }
        };
        setCartItems(prev => [...prev, newItem]);
      }
      setIsCartOpen(true);
    }
  };

  // Remove an item from the cart by cartItemId (room_id + ratecode)
  const removeFromCart = async (cartItemId) => {
    try {
      // Update last operation for UI refresh
      setLastOperation({
        type: 'remove',
        cartItemId: cartItemId,
        success: true,
        timestamp: new Date().getTime()
      });
      
      // Remove the item from cart using cartItemId
      setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
    } catch (error) {
      console.error('Error removing item from cart:', error);
      // Still remove from cart even if there's an error
      setCartItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
    }
  };

  // Clear all items from the cart
  const clearCart = async () => {
    try {
      // Update last operation for UI refresh
      setLastOperation({
        type: 'clear_all',
        count: cartItems.length,
        success: true,
        timestamp: new Date().getTime()
      });
      
      // Clear the cart
      setCartItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
      // Still clear the cart even if there's an error
      setCartItems([]);
    }
  };

  // Update room quantity in cart with availability check
  const updateRoomQuantity = (cartItemId, newQuantity) => {
    try {
      if (newQuantity < 1) {
        // If quantity is less than 1, remove the item
        removeFromCart(cartItemId);
        return { success: true };
      }

      // Find the item to check available rooms using cartItemId
      const item = cartItems.find(item => item.cartItemId === cartItemId);
      
      if (!item) {
        console.error('Room not found in cart');
        return { success: false, error: 'Room not found' };
      }

      // Check if new quantity exceeds available rooms
      const availableRooms = item.room_info?.rooms_available || item.rooms_available || 0;
      
      console.log('=== UPDATE QUANTITY VALIDATION ===');
      console.log('Cart Item ID:', cartItemId);
      console.log('New quantity:', newQuantity);
      console.log('Available rooms:', availableRooms);
      console.log('Item room_info:', item.room_info);
      console.log('Item availability:', item.availability);
      console.log('Full item:', item);
      console.log('==================================');
      
      if (newQuantity > availableRooms) {
        console.warn(`Cannot add more rooms. Only ${availableRooms} rooms available.`);
        
        // Update last operation with error
        setLastOperation({
          type: 'update_quantity_failed',
          cartItemId: cartItemId,
          quantity: newQuantity,
          maxQuantity: availableRooms,
          success: false,
          error: `Only ${availableRooms} room(s) available`,
          timestamp: new Date().getTime()
        });
        
        return { 
          success: false, 
          error: `Only ${availableRooms} room(s) available`,
          maxQuantity: availableRooms
        };
      }

      const updatedItems = cartItems.map(item => {
        if (item.cartItemId === cartItemId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });

      setCartItems(updatedItems);
      
      // Update last operation for UI refresh
      setLastOperation({
        type: 'update_quantity',
        cartItemId: cartItemId,
        quantity: newQuantity,
        success: true,
        timestamp: new Date().getTime()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating room quantity:', error);
      return { success: false, error: error.message };
    }
  };

  // Calculate the total cost of items in the cart
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      // Use promo-discounted price if available, otherwise use regular rate
      const roomRate = item.promoFinalPrice || item.rate_details?.final_rate || 0;
      const nights = item.nights || 1;
      const quantity = item.quantity || 1;
      return total + (roomRate * nights * quantity);
    }, 0);
  };
  
  // Calculate the original total (before promo discount)
  const getCartOriginalTotal = () => {
    return cartItems.reduce((total, item) => {
      const roomRate = item.rate_details?.final_rate || 0;
      const nights = item.nights || 1;
      const quantity = item.quantity || 1;
      return total + (roomRate * nights * quantity);
    }, 0);
  };
  
  // Calculate total promo discount
  const getCartPromoDiscount = () => {
    return cartItems.reduce((total, item) => {
      if (item.promo === 1 || item.activePromo) {
        // If totalDiscount is stored (from new calculation), use it directly
        if (item.promoTotalDiscount !== undefined) {
          return total + item.promoTotalDiscount;
        }
        
        // Fallback: Check discount type
        if (item.promoDiscountType === 'fixed_amount') {
          // Fixed amount - don't multiply by nights
          return total + (item.promoDiscount || 0);
        }
        
        // For percentage and free_night, multiply by nights and quantity
        const originalRate = item.rate_details?.final_rate || 0;
        const promoRate = item.promoFinalPrice || originalRate;
        const nights = item.nights || 1;
        const quantity = item.quantity || 1;
        return total + ((originalRate - promoRate) * nights * quantity);
      }
      return total;
    }, 0);
  };

  // Get the number of items in the cart
  const getCartCount = () => {
    return cartItems.length;
  };

  // Get total room quantity in cart
  const getTotalRoomQuantity = () => {
    return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  };

  // Toggle cart visibility
  const toggleCart = () => {
    setIsCartOpen(prev => !prev);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        updateRoomQuantity,
        getCartTotal,
        getCartOriginalTotal,
        getCartPromoDiscount,
        getCartCount,
        getTotalRoomQuantity,
        isCartOpen,
        setIsCartOpen,
        toggleCart,
        lastOperation
      }}
    >
      {children}
    </CartContext.Provider>
  );
}; 